/**
 * Various untilies to encode, manipulate and convert between values of various units.
 */


class Distance {
  static get units() {
    return ["mi", "km"];
  }

  constructor(value, unit = "mi") {
    this.value = value.valueOf();
    this.unit = unit;
  }

  valueOf() {
    return this.value;
  }

  toString() {
    return `${Math.round(this.value * 10) / 10}${this.unit}`;
  }

  asRounded(unit) {
    return Math.round(this.as(unit) * 10) / 10;
  }

  as(unit) {
    if (!Distance.units.includes(unit)) {
      throw new Error(`'${unit}' is not a valid unit`);
    }
    return Distance.convert(this.value, this.unit, unit).valueOf();
  }

  into(unit) {
    return new Distance(this.as(unit), unit);
  }

  static convert(value, from, to) {
    if (!(typeof value === "number" || value instanceof Distance)) {
      throw new Error(`'${value}' is not a valid value`);
    }
    if (!Distance.units.includes(from)) {
      throw new Error(`'${from}' is not a valid unit`);
    }
    if (!Distance.units.includes(to)) {
      throw new Error(`'${to}' is not a valid unit`);
    }

    if (from === to) {
      return value;
    }

    const MI_OVER_KM = 1.609344;

    switch (`${from} -> ${to}`) {
      case "mi -> km":
        return value * MI_OVER_KM;

      case "km -> mi":
        return value / MI_OVER_KM;

      default:
        throw new Error("Unreachable");
    }
  }
}

class Temperature {
  static get units() {
    return ["c", "f"];
  }

  constructor(value, unit = "f") {
    this.value = value.valueOf();
    this.unit = unit;
  }

  valueOf() {
    return this.value;
  }

  toString() {
    return `${Math.round(this.value * 10) / 10} °${this.unit.toUpperCase()}`;
  }

  asRounded(unit) {
    return Math.round(this.as(unit) * 10) / 10;
  }

  as(unit) {
    if (!Temperature.units.includes(unit)) {
      throw new Error(`'${unit}' is not a valid unit`);
    }
    return Temperature.convert(this.value, this.unit, unit).valueOf();
  }

  into(unit) {
    return new Temperature(this.as(unit), unit);
  }

  static convert(value, from, to) {
    if (!(typeof value === "number" || value instanceof Temperature)) {
      throw new Error(`'${value}' is not a valid value`);
    }
    if (!Temperature.units.includes(from)) {
      throw new Error(`'${from}' is not a valid unit`);
    }
    if (!Temperature.units.includes(to)) {
      throw new Error(`'${to}' is not a valid unit`);
    }

    if (from === to) {
      return value;
    }

    switch (`${from} -> ${to}`) {
      case "f -> c":
        return (value - 32) * (5 / 9);

      case "c -> f":
        return value * (9 / 5) + 32;

      default:
        throw new Error("Unreachable");
    }
  }
}

class Weight {
  static get units() {
    return ["lbs", "kgs", "meals"];
  }

  constructor(value, unit = "lbs") {
    this.value = value.valueOf();
    this.unit = unit;
  }

  valueOf() {
    return this.value;
  }

  toString() {
    return `${Math.round(this.value * 10) / 10} ${this.unit}`;
  }

  as(unit) {
    if (!Weight.units.includes(unit)) {
      throw new Error(`'${unit}' is not a valid unit`);
    }
    return Weight.convert(this.value, this.unit, unit).valueOf();
  }

  asRounded(unit) {
    return Math.round(this.as(unit) * 10) / 10;
  }

  into(unit) {
    return new Weight(this.as(unit), unit);
  }

  static convert(value, from, to) {
    if (!(typeof value === "number" || value instanceof Weight)) {
      throw new Error(`'${value}' is not a valid value`);
    }
    if (!Weight.units.includes(from)) {
      throw new Error(`'${from}' is not a valid unit`);
    }
    if (!Weight.units.includes(to)) {
      throw new Error(`'${to}' is not a valid unit`);
    }

    if (from === to) {
      return value;
    }

    const KGS_OVER_LBS = 0.45359237; // kg   = lb * KGS_OVER_LBS
    const MEALS_OVER_LBS = 1.2; // meal = lb * MEALS_OVER_LBS

    switch (`${from} -> ${to}`) {
      case "kgs -> lbs":
        return value / KGS_OVER_LBS;

      case "lbs -> kgs":
        return value * KGS_OVER_LBS;

      case "meals -> lbs":
        return value * MEALS_OVER_LBS;

      case "lbs -> meals":
        return value / MEALS_OVER_LBS;

      case "kgs -> meals":
        return value / KGS_OVER_LBS / MEALS_OVER_LBS;

      case "meals -> kgs":
        return value * MEALS_OVER_LBS * KGS_OVER_LBS;

      default:
        throw new Error(`Unreachable ${from} -> ${to}`);
    }
  }
}

/**
 * Determine default unit based on team's country.
 * @params {string} user.account_type
 * @params {string | null} user.country Team's country
 * @returns boolean
 */
const determineDefaultUnit = (user) => {
  const { account_type, country, isCorporate } = user;

  const imperialCountries = [
    "United States",
    "Puerto Rico",
    "U.S. Virgin Islands",
    "Guam",
    "Palau",
    "Belize",
    "Bahamas",
    "Cayman Islands",
    "Liberia",
    "Myanmar",
  ];

  return (
    account_type === "ADMIN" ||
    imperialCountries.includes(country) ||
    isCorporate
  );
};

/**
 * Given the `currentUser`, return the units preferred by that user based on
 * their location.
 */
const getLocaleUnit = (currentUser) => {
  const imperial = determineDefaultUnit({
    account_type: currentUser.account_type,
    country: currentUser.country,
  });

  return {
    weight: imperial ? "lbs" : "kgs",
    temp: imperial ? "f" : "c",
    distance: imperial ? "mi" : "km"
  };
};

module.exports = {
  Distance,
  Temperature,
  Weight,
  getLocaleUnit,
  determineDefaultUnit,
};